import { toastManager } from "../ui/toast";
import type { OrchestrationCommand } from "@cmmd-forge/contracts";
import type { Project, ProjectScript } from "../../types";
import type { NewProjectScriptInput } from "../ProjectScriptsControl.types";
import { readEnvironmentApi } from "../../environmentApi";
import { ensureLocalApi } from "../../localApi";
import { commandForProjectScript, nextProjectScriptId } from "../../projectScripts";
import { decodeProjectScriptKeybindingRule } from "../../lib/projectScriptKeybindings";
import { newCommandId } from "../../lib/utils";
import { selectProjectByRef, useStore } from "../../store";
import { scopeProjectRef } from "@cmmd-forge/client-runtime";

export function createThreadProjectScriptActions(project: Project) {
  const currentScripts = () =>
    selectProjectByRef(useStore.getState(), scopeProjectRef(project.environmentId, project.id))
      ?.scripts ?? project.scripts;
  const save = async (
    mutation: Extract<OrchestrationCommand, { type: "project.script.update" }>["mutation"],
  ) => {
    const api = readEnvironmentApi(project.environmentId);
    if (!api) throw new Error("Connect to this environment to edit project actions.");
    await api.orchestration.dispatchCommand({
      type: "project.script.update",
      commandId: newCommandId(),
      projectId: project.id,
      mutation,
    });
  };

  return {
    add: async (input: NewProjectScriptInput) => {
      const scripts = currentScripts();
      const id = nextProjectScriptId(
        input.name,
        scripts.map((script) => script.id),
      );
      const { keybinding: _keybinding, ...script } = input;
      decodeProjectScriptKeybindingRule({
        command: commandForProjectScript(id),
        keybinding: input.keybinding,
      });
      await save({ kind: "add", script: { ...script, id } });
      await saveShortcut(id, input);
    },
    update: async (id: string, input: NewProjectScriptInput, expected: ProjectScript) => {
      const { keybinding: _keybinding, ...script } = input;
      decodeProjectScriptKeybindingRule({
        command: commandForProjectScript(id),
        keybinding: input.keybinding,
      });
      await save({ kind: "update", expected, script: { ...script, id } });
      await saveShortcut(id, input);
    },
    remove: async (id: string, expected: ProjectScript) => {
      await save({ kind: "remove", expected });
      await ensureLocalApi()
        .server.removeKeybinding({ command: commandForProjectScript(id) })
        .catch(() => {
          toastManager.add({
            type: "warning",
            title: "Action removed, shortcut could not be cleared",
            description: "Remove its shortcut in Settings.",
          });
        });
    },
  };
}

async function saveShortcut(id: string, input: NewProjectScriptInput) {
  const command = commandForProjectScript(id);
  const rule = decodeProjectScriptKeybindingRule({ command, keybinding: input.keybinding });
  try {
    if (rule) await ensureLocalApi().server.upsertKeybinding(rule);
    else await ensureLocalApi().server.removeKeybinding({ command });
  } catch (error) {
    toastManager.add({
      type: "warning",
      title: "Action saved, shortcut could not be updated",
      description:
        error instanceof Error ? error.message : "Try updating the shortcut in Settings.",
    });
  }
}
